import async from 'async';

import ObjectUtils from '../../util/object_utils';
import Browser from '../../modules/browser/browser';
import config from '../../config';
import {ErrorHandler, DEFAULT_ERROR} from '../../modules/error_handler';
import HttpUtils from '../../util/http_utils';
import StringUtils from '../../util/string_utils';
import StarzPlayerConstants from '../../starzplayer_constants';
import ERRORS from './errors';

/**
 * todo: The feed parser needs a thorough revision
 * @type {{}}
 */
let Feed = {};

let PROTECTION_SCHEMES = ['widevine', 'fairplay', 'playready'];
/**
 *
 * @param feedUri
 * @param {Object} options
 * @param options.trailer
 * @param {String} options.videoType
 * @param {String} options.globalUserId
 * @param [options.season] if not chosen and latest=false, reinitialize season will be selected
 * @param [options.episode] if not chosen and latest=false, reinitialize episode will be selected
 * @param [options.latest=false]
 * @param [options.playNext=false]
 * @param {function} callback
 * @param {boolean} callback.success
 * @param {Object} callback.feedInfo
 */
Feed.parse = function(feedUri, options, callback) {

    switch (options.videoType) {
        case StarzPlayerConstants.MOVIE:
            getFeedFromEsb(feedUri, function(error, feed) {
                error ?
                    callback(false, feed) :
                    callback(true, parseToFeedInfo(options.globalUserId, options.videoType, feed, {trailer: options.trailer || false, start: options.start || false, end: options.end || false}));
            });
            break;
        case StarzPlayerConstants.SERIES:
            getFeedInfoByUriForSeries(feedUri, options, function(success, feed) {
                success ?
                    callback(true, feed) :
                    ErrorHandler.throwError(ERRORS.GENERAL_PARSING_ERROR, {
                        reason: 'The series feedInfo returned false.',
                        feedInfo: feed
                    });
            });
            break;
        default:
            ErrorHandler.throwError(ERRORS.INVALID_VIDEO_TYPE, {
                reason: 'Invalid video type',
                given: options ? options.videoType : 'Options does not exist',
                expected: [StarzPlayerConstants.MOVIE, StarzPlayerConstants.SERIES]
            });
    }
};

Feed.getFirstEpisodeFeedUrl = function(seriesFeedUrl, season, lang = 'en') {
    const feedUriSplit = seriesFeedUrl.split('?');
    return feedUriSplit[0] + (feedUriSplit[0].slice(-1) === '/' ? '' : '/') + 'seasons/' + season + '/episodes/1/?' +
        feedUriSplit[1] + '&fields=thumbnails,sortTitle,tvSeasonNumber,tvSeasonEpisodeNumber,description';
};

Feed.getUserMedialistsPath = function(globalUserId, listId, itemId) {
    if (itemId !== undefined) {
        return config.esbUrl + 'userAccounts/' + globalUserId + '/mediaLists/' + listId + '/items/' + itemId;
    } else if (listId !== undefined) {
        return config.esbUrl + 'userAccounts/' + globalUserId + '/mediaLists/' + listId + '/items';
    } else if (globalUserId !== undefined) {
        return config.esbUrl + 'userAccounts/' + globalUserId + '/mediaLists';
    }
    return -1;
};

Feed.getUserMediaListByTitleIdUri = function(globalUserId, titleId) {
    if (globalUserId !== undefined && titleId !== undefined) {
        return config.esbUrl + 'userAccounts/' + globalUserId + '/mediaLists/items/' + titleId;
    }
    return -1;
};

/**
 * Returns an object with the feedInformation
 * @param userId
 * @param {Object} esbResponseFeed, the feed of the object to playback, like an episode or a movie
 * @param {Object} [options={}] the feed of the series, if applicable.
 * @param {Object} [options.esbResponseSeries] the feed of the series, if applicable.
 * @param {Boolean} [options.trailer] the feed of the series, if applicable.
 * @param {Boolean} [options.trailer] the feed of the series, if applicable.
 * @param videoType
 * @return {Object}
 */
function parseToFeedInfo(userId, videoType, esbResponseFeed, options) {
    const assetTypes = ObjectUtils.values(Browser.getSupportedAssetTypes(videoType, {trailer: options.trailer}));
    const currentTitle = esbResponseFeed['titles'][0];
    const playbackConfiguration = getPlaybackDrmConfiguration(assetTypes, currentTitle['media'][0]['content'], {start: options.start, end: options.end});
    const resourceUrl = getResourceUrlByProtectionScheme(currentTitle['media'][0]['content']);
    const currentMedia = getAssociatedProtectedMediaScheme(currentTitle['media'][0]['content']);

    let feedInfo = {
        globalUserId: userId,
        title: currentTitle['title'],
        titleId: currentTitle['titleId'],
        shortDescription: currentTitle['shortDescription'] || currentTitle['description'],
        longDescription: currentTitle['longDescription'],
        videoType: videoType,
        trailer: options.trailer,
        url: playbackConfiguration.url,
        drm: playbackConfiguration.drm,
        filmstrip: playbackConfiguration.filmstrip,
        ageRating: currentTitle['arAgeRating'],
        resource: resourceUrl,
        analyticsInfo: {
            userBitrate: currentMedia.bitrate,
            titleEn: currentTitle['titleLocalized'] && currentTitle['titleLocalized']['en'] ?
                currentTitle['titleLocalized']['en'] : currentTitle['title'],
            genre: currentTitle['tags'][0].tagTitle,
            duration: parseInt(currentMedia.duration),
            mediaGuid: currentTitle.media[0].mediaGuid
        }
    };

    if (options.esbResponseSeries) {
        feedInfo['seriesFeedInfo'] = {
            seriesId: options.esbResponseSeries['titles'][0]['titleId'],
            title: options.esbResponseSeries['titles'][0]['title'],
            episode: options.episode,
            season: options.season,
            seasons: [],
            nextEpisode: options.nextEpisode,
            nextSeason: options.nextSeason
        };

        let optionsResponseSeries = options['esbResponseSeries']['titles'][0];
        feedInfo['analyticsInfo'] = {
            userBitrate: feedInfo.analyticsInfo.userBitrate || optionsResponseSeries.media[0].content[0].bitrate,
            titleEn: optionsResponseSeries['titleLocalized'] && optionsResponseSeries['titleLocalized']['en'] ? optionsResponseSeries['titleLocalized']['en'] : optionsResponseSeries['title'],
            genre: optionsResponseSeries['tags'][0].tagTitle,
            duration: feedInfo.analyticsInfo.duration || parseInt(optionsResponseSeries.media[0].content[0].duration),
            mediaGuid: optionsResponseSeries.media[0].mediaGuid
        };
    }

    return feedInfo;
}

// Todo: write just one parser and not 4, add it to the parseURIData function

Feed.getSeasonFeed = function(seriesFeedUrl) {
    var feedUriSplit = seriesFeedUrl.split('?');
    return feedUriSplit[0] + (feedUriSplit[0].slice(-1) === '/' ? '' : '/') + 'seasons/?' + feedUriSplit[1];
};

Feed.getEpisodesFeedUrl = function(seriesFeedUrl, season) {
    var feedUriSplit = seriesFeedUrl.split('?');
    return feedUriSplit[0] + (feedUriSplit[0].slice(-1) === '/' ? '' : '/') + 'seasons/' + season + '/episodes/?' +
        feedUriSplit[1] + '&fields=titleId,title,sortTitle,description,thumbnails,tvSeasonNumber,tvSeasonEpisodeNumber';
};

function getEpisodeFeedUrl(seriesFeedUrl, season, episode) {
    var feedUriSplit = seriesFeedUrl.split('?');
    return feedUriSplit[0] + (feedUriSplit[0].slice(-1) === '/' ? '' : '/') +
        'seasons/' + season + '/episodes/' + episode + '/?' + feedUriSplit[1];
}

/**
 *
 * @param esbFeed
 * @return {{firstSeason: number, firstEpisode: number, seasons: Object}}
 */
function getSeasonsAndEpisodesInformation(esbFeed) {
    const media = esbFeed['titles'][0]['media'];

    let seriesInfo = {
        firstSeason: 0,
        firstEpisode: 0,
        seasons: {}
    };

    for (let i = 0; i < media.length; i++) {
        const entry = /([0-9]+)E([0-9]*)S/.exec(StringUtils.reverse(media[i]['mediaGuid']));
        const season = parseInt(StringUtils.reverse(entry[2]));
        const episode = parseInt(StringUtils.reverse(entry[1]));

        // Add season and episode
        if (!seriesInfo.seasons[season]) {
            seriesInfo.seasons[season] = [episode];
        } else if (seriesInfo.seasons[season] && seriesInfo.seasons[season].indexOf(episode) === -1) {
            seriesInfo.seasons[season].push(episode);
        }

        // Primary episode information
        if ((season <= seriesInfo.firstSeason && episode < seriesInfo.firstEpisode) || (!seriesInfo.firstEpisode || !seriesInfo.firstSeason)) {
            seriesInfo.firstSeason = season;
            seriesInfo.firstEpisode = episode;
        }
    }

    return seriesInfo;
}

Feed.getMainTitleId = function(path) {
    let mainTitleId = '';
    if (path.indexOf('/series') > -1) {
        mainTitleId = parseInt(path.split('?')[0].split('series/')[1]);
        return mainTitleId;
    } else if (path.indexOf('/movies') > -1) {
        mainTitleId = parseInt(path.split('?')[0].split('movies/')[1]);
        return mainTitleId;
    }
    return -1;
};

/**
 *
 * @param seriesUri
 * @param options
 * @param {function} callback
 * @param {boolean} callback.success
 * @param {object} callback.feedInfo
 */
function getFeedInfoByUriForSeries(seriesUri, options, callback) {
    const getSeriesFeedInfo = callback => getFeedFromEsb(seriesUri, callback);
    const createEpisodeFeedUri = (seriesFeedInfo, callback) => {
        let season = parseInt(options.season);
        let episode = parseInt(options.episode);
        let nextEpisode, nextSeason;

        const episodesInfo = getSeasonsAndEpisodesInformation(seriesFeedInfo);
        if (!episode || !season) {
            season = parseInt(episodesInfo.firstSeason);
            episode = parseInt(episodesInfo.firstEpisode);
        }

        if (episodesInfo.seasons[season].indexOf(episode + 1) > -1) {
            nextSeason = season;
            nextEpisode = episode + 1;
        } else if (episodesInfo.seasons[season + 1]) {
            nextSeason = season + 1;
            nextEpisode = Math.min.apply(null, episodesInfo.seasons[season + 1]);
        }

        callback(null, seriesFeedInfo, {
            uri: getEpisodeFeedUrl(seriesUri, season, episode),
            season: season,
            episode: episode,
            seasons: episodesInfo.seasons,
            nextEpisode: nextEpisode,
            nextSeason: nextSeason
        });
    };
    const getEpisodeFeedInfo = (seriesFeedInfo, episodeInfo, callback) => getFeedFromEsb(episodeInfo.uri, (err, episodeFeedData) => {
        return callback(err, episodeFeedData, seriesFeedInfo, episodeInfo);
    });

    async.waterfall([
        getSeriesFeedInfo,
        createEpisodeFeedUri,
        getEpisodeFeedInfo
    ], function onDone(err, episodeFeedInfo, seriesFeedInfo, episodeInfo) {
        err ?
            ErrorHandler.throwError(ERRORS.GENERAL_PARSING_ERROR, {
                reason: 'Waterfall returned error',
                error: episodeFeedInfo
            }) :
            callback(true, parseToFeedInfo(options.globalUserId, options.videoType, episodeFeedInfo, {
                esbResponseSeries: seriesFeedInfo,
                trailer: options.trailer || false,
                episode: episodeInfo.episode,
                season: episodeInfo.season,
                nextEpisode: episodeInfo.nextEpisode,
                nextSeason: episodeInfo.nextSeason
            }));

    });
}

function getFeedFromEsb(feedUri, callback) {
    HttpUtils.get({
        uri: feedUri,
        headers: {
            'client-type': 'website',
            'xtype': 'portal'
        }
    }, function onReply(success, data) {

        data = typeof data === 'string' && ObjectUtils.isJsonString(data) ? JSON.parse(data) : data;

        if (typeof data !== 'object') {
            ErrorHandler.throwError(ERRORS.UNEXPECTED_ESB_RESPONSE, {
                reason: 'ESB is supposed to return JSON. Reponse doesn\'t contain JSON. ',
                response: data
            });
        }
        if (data.details && data.details.indexOf('unable to resolve variable \'geolocatedCountry\'') > -1) {
            ErrorHandler.throwError(ERRORS.GEO_BLOCK, {
                reason: 'Invalid country',
                response: data
            });
        }
        if (!success) {
            ErrorHandler.throwError(ERRORS.UNEXPECTED_ESB_RESPONSE, {
                reason: 'Feed request unsuccessful.',
                response: data,
                uri: feedUri
            });
        }
        if (!(data && data['titles'] && data['titles'].length === 1)) {
            ErrorHandler.throwError(ERRORS.EMPTY_FEED, {feed: feedUri});
        }

        callback(null, data);
    });
}

/**
 * Iterates over the videoContentsArray of the feed to get the drm playback configuration
 * @param drmTypes
 * @param feedContentArray
 * @return {{drm: {}, url: {}, filmstrip: null}}
 */
function getPlaybackDrmConfiguration(drmTypes, feedContentArray, options = {}) {
    var videoContent = {
        drm: {},
        url: {},
        filmstrip: null
    };
    for (var i = 0; i < feedContentArray.length; i++) {
        var content = feedContentArray[i];
        var assetTypes = content['assetTypes'];
        if (assetTypes) { // This content has asset types (DRM)
            for (var j = 0; j < assetTypes.length; j++) {
                var assetType = assetTypes[j];

                if ((drmTypes[0] && drmTypes[0].indexOf(assetType) > -1) ||
                        (drmTypes && drmTypes.indexOf(assetType) > -1)) {
                    // Check if the assetType (protectionScheme) is in the drmTypes that we need

                    var typeArray = assetType.split('_');
                    var protectionScheme = typeArray[1]; // e.g. Widevine
                    var drmTech = typeArray[0] === 'dash' && protectionScheme === 'primetime' ? 'flash' : typeArray[0]; // e.g. dash, or flash when primetime

                    // TODO: Validate check if all values are valid

                    videoContent.drm[assetType] = {
                        protectionScheme: protectionScheme,
                        drmTech: drmTech,
                        pid: content['releases'][0]['pid'],
                        selectorServiceURL: content['releases'][0]['url'].replace('http', 'https')
                    };

                    // TODO: TEMPORY FIX FOR HTTPS!!
                    var drmTechUrl = content['streamingUrl'].replace(config.httpJitUrl, config.httpsJitUrl);
                    var drmTechUrl2 = drmTechUrl.replace(config.httpJitUrl2, config.httpsJitUrl2);
                    videoContent.url[drmTech] = drmTechUrl2.replace(config.httpJitUrl3, config.httpsJitUrl3);

                    var start = options.start;
                    var end = options.end;
                    var catchupData = "?begin=" + start + "&end=" + end;
                    var catchup = start && start != undefined ? catchupData : '';
                    if (catchup) {
                        videoContent.url[drmTech] = videoContent.url[drmTech] + catchup;
                    }

                    const lbCdnHost = window.sessionStorage.getItem('lbCDN_Host');
                    if (!!lbCdnHost) {
                        videoContent.url[drmTech] = videoContent.url[drmTech].replace(config.httpsJitUrl, lbCdnHost);
                    }

                } else if (assetType === 'filmstrip_199x110') {
                    videoContent.filmstrip = content['streamingUrl'].replace(config.httpCdnUrl, config.httpsCdnUrl);
                }
            }
        }
    }
    return videoContent;
}

/**
 * Return the associated media with the current protectionScheme
 * @param media {Array} of object with all the possible medias
 * @return {Object} The associated object with the current assetType
 */
function getAssociatedProtectedMediaScheme(media) {
    for (var i = 0; i < media.length; i++) {
        if (PROTECTION_SCHEMES.indexOf(media[i]['protectionScheme']) > -1) {
            return media[i];
        }
    }
    // Fallback if there is no associated mediaassettype to return the first item
    return media[0] || {};
}


/**
 * Return the associated media with the current assetType
 * @param assetType {String} that describe the selected asset type
 * @param allMedia {Array} of object with all the possible medias
 * @return {Object} The associated object with the current assetType
 */
function getAssociatedAssetTypeMedia(assetType, allMedia) {
    for (var i = 0; i < allMedia.length; i++) {
        if (allMedia[i]['assetTypes'][0] === assetType) {
            return allMedia[i];
        }
    }
    // Fallback if there is no associated mediaassettype to return the first item
    return allMedia[0] || {};
}

/**
 * Get resourceUrl asocialted with the selected assets
 * @param protectionScheme
 * @param feedContentArray
 * @return {Resource Url}
 */
function getResourceUrlByProtectionScheme(feedContentArray) {
    var streamingUrl = '';
    for (var i = 0; i < feedContentArray.length; i++) {
        if (PROTECTION_SCHEMES.indexOf(feedContentArray[i]['protectionScheme']) > -1) {
            streamingUrl = feedContentArray[i]['releases'][0]['url'] || '';
            return streamingUrl;
        }
    }
    return '';
}

/**
 * Get resourceUrl asocialted with the selected assets
 * @param drmTypes
 * @param feedContentArray
 * @return {Resource Url}
 */
function getResourceUrl(drmTypes, feedContentArray) {
    var streamingUrl = '';
    var assetType = drmTypes[0] || '';
    for (var i = 0; i < feedContentArray.length; i++) {
        if (assetType === feedContentArray[i]['assetTypes'][0]) {
            streamingUrl = feedContentArray[i]['releases'][0]['url'] || '';
            return streamingUrl;
        }
    }
    return '';
}

module.exports = Feed;



// WEBPACK FOOTER //
// ./src/modules/feed/feed.js